import * as React from "react";
import { AdminLevelList, ConsultantLevelList, doesUserHaveAccess } from "../../model/UserDetails.model";
import { UserTypeEnum } from "../../model/UserInfo.model";
import { handleEnter } from "../../utils/commonUtil";
import { OktaLogOut } from "./OktaLogout";
import { getConfig } from "../../utils/envUtil";
import {useNavigate} from "react-router-dom";
import {navigateTo} from "../../../navigateTo";

export interface IMenuProps {
    username?: string;
    shown: boolean;
    closeMenuFn: () => void;
    userType: UserTypeEnum;
    hasRaAccess: boolean;
    setCorsErrorModalOpen: (is: boolean) => void;
}

const truncateUsernameText = (username: string): string => username.substring(0, 25);

export const Menu: React.FC<IMenuProps> = (props) => {
    const navigate = useNavigate();
    const getTabIndex = () => (props.shown ? 0 : -1);

    const navigateAndCloseMenu = (path: string) => {
        navigateTo(path, navigate);
        props.closeMenuFn();
    };

    React.useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape" && props.shown) props.closeMenuFn();
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [props.shown, props.closeMenuFn]);

    const hasAdminAccess = doesUserHaveAccess(props.userType, AdminLevelList);
    const hasMyClientsAccess = doesUserHaveAccess(props.userType, [...ConsultantLevelList, UserTypeEnum.ATC_ADMIN]);

    const renderAdminLinks = () => {
        if (!hasAdminAccess) return null;
        return (
            <div className="menu__group">
                <div className="menu__group-label">Administration</div>
                <a className="menu__item admin-page-link old-anchor" tabIndex={getTabIndex()}
                   onKeyUp={handleEnter(() => navigateAndCloseMenu("/admin"))}
                   onClick={() => navigateAndCloseMenu("/admin")}>
                    <i className="fa-light fa-user-gear" aria-hidden="true" />
                    User Administration
                </a>
                <a className="menu__item admin-client-plan-link old-anchor" tabIndex={getTabIndex()}
                   onKeyUp={handleEnter(() => navigateAndCloseMenu("/admin-client-plan"))}
                   onClick={() => navigateAndCloseMenu("/admin-client-plan")}>
                    <i className="fa-light fa-briefcase" aria-hidden="true" />
                    Client Plan Administration
                </a>
                <a className="menu__item admin-upload-page-link old-anchor" tabIndex={getTabIndex()}
                   onKeyUp={handleEnter(() => navigateAndCloseMenu("/admin-upload"))}
                   onClick={() => navigateAndCloseMenu("/admin-upload")}>
                    <i className="fa-light fa-upload" aria-hidden="true" />
                    Upload Files
                </a>
                <a className="menu__item admin-media-groups-page-link old-anchor" tabIndex={getTabIndex()}
                   onKeyUp={handleEnter(() => navigateAndCloseMenu("/admin-media-groups"))}
                   onClick={() => navigateAndCloseMenu("/admin-media-groups")}>
                    <i className="fa-light fa-photo-film" aria-hidden="true" />
                    CPR Media Group Admin
                </a>
            </div>
        );
    };

    const renderRiskAnalyzerLink = () => {
        if (!props.hasRaAccess) return null;
        return (
            <div className="menu__group">
                <a className="menu__item risk-analyzer-link old-anchor" tabIndex={getTabIndex()}
                   onClick={() => window.open(getConfig().FRONTEND_CONFIG_RISK_ANALYZER_URL, "_blank")}
                   onKeyUp={handleEnter(() => window.open(getConfig().FRONTEND_CONFIG_RISK_ANALYZER_URL, "_blank"))}>
                    <i className="fa-light fa-chart-line" aria-hidden="true" />
                    Risk Analyzer
                </a>
            </div>
        );
    };

    const renderMyClientsPageLink = () => {
        if (!hasMyClientsAccess) return null;
        return (
            <div className="menu__group">
                <a className="menu__item old-anchor" id="my-clients-link" tabIndex={getTabIndex()}
                   onKeyUp={handleEnter(() => navigateAndCloseMenu("/my-clients"))}
                   onClick={() => navigateAndCloseMenu("/my-clients")}>
                    <i className="fa-light fa-users" aria-hidden="true" />
                    My Clients
                </a>
            </div>
        );
    };

    const getMenuClass = () => "header__menu--" + (props.shown ? "shown" : "hidden");

    if (!props.username) {
        return <div />;
    }

    return (
        <div id="user-menu__container" data-testid="user-menu__container">
            <div id="menu" className={getMenuClass()} data-testid="menu">
                <div className="menu__header">
                    <div className="menu__avatar">
                        <i className="fa-light fa-user" aria-hidden="true" />
                    </div>
                    <div className="menu__username" id="username" data-testid="username">
                        {truncateUsernameText(props.username)}
                    </div>
                </div>
                <div className="menu__body">
                    {renderMyClientsPageLink()}
                    {renderAdminLinks()}
                    {renderRiskAnalyzerLink()}
                    <div className="menu__group">
                        <OktaLogOut setCorsErrorModalOpen={props.setCorsErrorModalOpen} />
                    </div>
                </div>
            </div>
        </div>
    );
};
